import { Cart } from "@omni/types/api/cart/bff/cart.model";
import { CartItem } from "@omni/types/api/cart/bff/shared/cartItem.model";
import { BoxConditionModel } from "@omni/types/components/cart/shared/boxCondition.model";
import { ComponentsBoxConditionType } from "@omni/types/components/cart/shared/enums/boxConditionType.enum";
import { ProductConfiguration } from "@omni/types/utils/productConfiguration";
import { Translate } from "@omni/types/utils/translate";
import { pick } from "lodash-es";

export const productIsAdditionalService = (
    items: Array<CartItem>,
    id?: string | null
) => {
    return (
        !!id &&
        items.some((i) => i.additionalServices?.map((as) => as.id).includes(id))
    );
};

export const getTitle = (orderItem: CartItem, t: Translate) => {
    if (orderItem.articleConfiguration) {
        return orderItem.articleConfiguration.title || "";
    }

    const name = orderItem.product?.productTitle;
    if (name === null || orderItem.warrantyDeposit) {
        return t("general.product.pallet.title");
    }

    return name;
};

export const getDiscountTotal = (cart?: Cart | null) => {
    if (!cart) {
        return null;
    }
    return (cart.globalDiscounts || 0) > 0 ? cart.globalDiscounts : null;
};

export const getGiftCardTotal = (cart?: Cart | null) => {
    if (!cart) {
        return null;
    }
    return cart.giftCardPayments.length > 0 ? cart.paidByGiftCards : null;
};

export const getConfiguration = (
    cartItem: CartItem,
    productConfigurations: Array<ProductConfiguration>
) => {
    return pick(
        cartItem,
        productConfigurations.reduce(
            (acc, pc) => [...acc, ...pc.configPaths],
            [] as string[]
        )
    );
};

export const getUrl = (
    cartItem: CartItem,
    productConfigurations: Array<ProductConfiguration>,
    giftcardPage: string
) => {
    if (cartItem?.product?.giftCardArticle && cartItem?.id) {
        return `${giftcardPage}?cartid=${cartItem.id}`;
    }

    if (
        Object.keys(getConfiguration(cartItem, productConfigurations) || {})
            .length &&
        cartItem?.id
    ) {
        return `${cartItem.product?.url}?cartid=${cartItem.id}`;
    }

    return cartItem.product?.url;
};

export const showStrippedPrice = (cartItem: CartItem) => {
    return (
        !!cartItem.articleStrikethroughPrice &&
        cartItem.articleSalesPrice !== cartItem.articleStrikethroughPrice &&
        !cartItem.free
    );
};

const componentsBoxConditionTypeMap: Record<
    string,
    ComponentsBoxConditionType
> = {
    "ageCondition": ComponentsBoxConditionType.AgeCondition,
    "contestCondition": ComponentsBoxConditionType.ContestConditions,
    "partnerCondition": ComponentsBoxConditionType.PartnerConditions
};

export const getActiveBoxConditions = (cart?: Cart) => {
    if (!cart?.conditions) {
        return [];
    }

    const conditions = cart.conditions;

    return (Object.keys(conditions) as Array<keyof typeof conditions>).reduce<
        Array<BoxConditionModel>
    >((arr, key) => {
        if (conditions[key].enabled) {
            arr.push({
                name: componentsBoxConditionTypeMap[key],
                ...cart.conditions![key]
            });
        }
        return arr;
    }, []);
};

export const getServiceTotal = (cart?: Cart | null) => {
    if (!cart) {
        return null;
    }
    const additionalServiceCost = cart.items
        .filter((i) => productIsAdditionalService(cart.items, i.id))
        .reduce((acc, curr) => {
            acc += curr.totalPrice;
            return acc;
        }, 0);

    return additionalServiceCost > 0 ? additionalServiceCost : null;
};
